import {
    useAvailableWorkspaces,
    useCurrentOrganization,
    useCurrentWorkspace,
} from "@ice/react";
import { useEffect, useState } from "react";

type AvailableWorkspaces = ReturnType<typeof useAvailableWorkspaces>;

export type TenantWorkspace = AvailableWorkspaces["workspaces"][number];

interface UseTenantWorkspaceResult {
    workspace: TenantWorkspace | undefined;
    isLoading: boolean;
    isError: boolean;
    error: string | null;
}

/**
 * The app's "tenant" workspace: the current organization's own.
 *
 * `useCurrentWorkspace` returns the workspace selected in the shell, which may be a
 * client's: it is not the tenant the app must operate on. The SDK loads the
 * available workspaces with `ownerId = organizationId`, so the organization's own
 * is the only one with `itemId === organization.id`.
 *
 * `isLoading` takes three signals, because not one of them is trustworthy alone:
 *
 * - **`organization === null`** — nothing to match the list against yet.
 * - **`workspaces.isLoading`** — the SDK's own flag, and it covers far less than
 *   its name suggests. `restoreSessionUsecase` publishes `organizations.selected`
 *   BEFORE it awaits `retrieveWorkspaces()` and never raises the flag at all, so
 *   for the whole round trip the snapshot reads `{all: [], isLoading: false}`
 *   with an organization already in place. `selectOrganizationUsecase` does raise
 *   it, but its `Effect.ensuring` lowers it again in a snapshot of its own,
 *   published before the one that fills `all`.
 * - **empty list AND nothing selected in the shell** — the stand-in for "the
 *   list has not landed". Both usecases set `workspaces.selected` only after
 *   `all` is populated, so a null selection over an empty list means the round
 *   trip is still in flight. This is the only signal that closes the window
 *   above, and closing it is what stops ViewWrapper from unmounting the entire
 *   subtree onto DataNotAvailable and remounting it a tick later.
 *
 * That third signal cannot be trusted forever, though, and this is the catch: a
 * `retrieveWorkspaces` that FAILS looks exactly like one still in flight.
 * @ice/core never puts the failure in the snapshot, and `useAvailableWorkspaces`
 * reports `error` only when the SUBSCRIPTION itself breaks — so "empty list,
 * nothing selected" is both "still coming" and "never coming", and believing it
 * indefinitely means the app neither renders nor ever says why. Hence
 * `WORKSPACE_LIST_DEADLINE_MS`, started when the organization lands, past which
 * an empty list counts as arrived and ViewWrapper is free to reach its terminal
 * branch.
 *
 * The deadline is a bound on how long the empty list is believed, NOT an attempt
 * to outlast the SDK — `workspacesCache` gives each attempt a 5 s timeout and
 * retries 3 times with exponential backoff, so it can still be working some 27 s
 * in. It does not need to: giving up is a temporary answer, because a list that
 * lands afterwards is picked up on the next render and the app recovers on its
 * own. That is what makes 10 s a UX choice rather than a correctness cliff.
 *
 * `isError` is narrower than it reads, for the reason above: it means the
 * subscription failed, not the list. What it buys is an immediate exit — it
 * forces `isLoading` to false, so a broken session, which takes the organization
 * down with it, does not sit through the deadline first.
 *
 * Consumers must not infer anything from `workspace === undefined` while
 * `isLoading` is true.
 */
/**
 * How long "empty list, nothing selected" is taken to mean "still coming" once
 * the organization has arrived. Comfortably past a healthy round trip and past
 * the SDK's own 5 s per-attempt timeout, short enough not to read as hung. See
 * the hook's doc comment for why overshooting the SDK's full retry budget is
 * unnecessary.
 */
const WORKSPACE_LIST_DEADLINE_MS = 10_000;

export const useTenantWorkspace = (): UseTenantWorkspaceResult => {
    const [organization] = useCurrentOrganization();
    const [selectedWorkspace] = useCurrentWorkspace();
    const {
        workspaces,
        isLoading: areWorkspacesLoading,
        isError,
        error,
    } = useAvailableWorkspaces();

    // Armed when the organization lands, because that is when the SDK starts
    // fetching the list. What expired is recorded as WHICH organization it
    // expired for, rather than a boolean: re-arming on a change is then just a
    // tag that no longer matches, with no state to reset synchronously inside
    // the effect.
    const organizationId = organization?.id;
    const [expiredForOrganizationId, setExpiredForOrganizationId] = useState<
        string | null
    >(null);
    useEffect(() => {
        if (organizationId === undefined) return;
        const timer = setTimeout(
            () => setExpiredForOrganizationId(organizationId),
            WORKSPACE_LIST_DEADLINE_MS,
        );
        return () => clearTimeout(timer);
    }, [organizationId]);
    const isDeadlineExpired =
        organizationId !== undefined &&
        expiredForOrganizationId === organizationId;

    const workspace = organization
        ? workspaces.find(({ itemId }) => itemId === organization.id)
        : undefined;

    const hasNotLanded =
        organization === null ||
        areWorkspacesLoading ||
        (workspaces.length === 0 &&
            selectedWorkspace === null &&
            !isDeadlineExpired);

    return {
        workspace,
        // A reported failure wins over every "not landed yet" signal: none of
        // them will ever be satisfied afterwards, so without this the loader
        // would be permanent.
        isLoading: !isError && hasNotLanded,
        isError,
        error,
    };
};
